import React from 'react';
import {IconButton, Spinner, SpinnerSize} from '@fluentui/react';
import {IProductBundleItem} from '../../models/ProductBundles';
import {ISavedSearchRequest} from '../../models/Search';
import {IColumnDetails, GridSelectionMode} from '../../models/GridConfiguration';
import {useGetSavedSearchQuery} from '../../store/endpoints/SavedSearchEndpoints';
import {useGetSearchResultsQuery} from '../../store/Api';
import DataGrid from '../DataGrid/DataGrid';
import ProductBundleQuantityEntry from './ProductBundleItemQuantityEntry';

interface ISavedSearchBundleItemRowProps {
    item: IProductBundleItem;
    onQuantityChange: (quantity: number) => void;
    onRemove: () => void;
}

/**
 * The saved-search item at the top of a bundle's item list (pinned above groups). It renders like a
 * group table, but the heading (the saved search name) is not editable and there is exactly one product
 * row — whatever the saved search currently resolves to (its top search result). The product row is
 * rendered through the same DataGrid the group/ungrouped items use, so it is the same size as the other
 * bundle items. A bundle may hold at most one saved-search item; it is always required with no
 * optionality/default controls.
 */
const SavedSearchBundleItemRow: React.FC<ISavedSearchBundleItemRowProps> = (props) => {
    const savedSearch = useGetSavedSearchQuery(
        {id: props.item.savedSearchId ?? ''},
        {skip: !props.item.savedSearchId}
    );

    const criteria = savedSearch.data?.criteria;
    // Resolve the saved search to its current top product so the row reflects what it points at now.
    const results = useGetSearchResultsQuery(
        {catalog: criteria?.catalog, criteria: (criteria ?? {}) as ISavedSearchRequest},
        {skip: !criteria}
    );
    const resolvedProduct = results.data?.products?.[0];

    // A synthetic bundle item carrying the resolved product's display data + the saved-search item's
    // quantity, so the shared DataGrid columns render it identically to a normal bundle item.
    const displayItem: IProductBundleItem = {
        ...props.item,
        productId: resolvedProduct?.id ?? '',
        catalog: resolvedProduct?.catalog,
        sourceItem: {
            name: resolvedProduct?.name,
            imageUrl: resolvedProduct?.image,
        },
        vendors: resolvedProduct?.vendors,
    };

    const columns: IColumnDetails[] = [
        {
            identifier: 'Thumbnail',
            label: '',
            type: '',
            effectiveWidth: 40,
            required: true,
            component: (q: IProductBundleItem) =>
                <img src={q?.sourceItem?.imageUrl} alt={q?.sourceItem?.name ?? ''} style={{maxHeight: '40px', maxWidth: '40px', margin: '0 auto'}} />,
        },
        {
            identifier: 'name',
            label: 'Name',
            type: '',
            sortable: false,
            filterable: false,
            required: true,
            component: (q: IProductBundleItem) => <div>{q.sourceItem?.name ?? 'No matching product'}</div>,
        },
        {
            identifier: 'quantity',
            label: 'Quantity',
            type: '',
            sortable: false,
            filterable: false,
            required: true,
            effectiveWidth: 110,
            minimumWidth: 110,
            component: (i: IProductBundleItem) =>
                <ProductBundleQuantityEntry item={i} onChange={props.onQuantityChange} />,
        },
    ];

    return (
        <div
            style={{
                marginBottom: '16px',
                border: '1px solid #edebe9',
                borderRadius: '4px',
                padding: '12px',
            }}
        >
            <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px'}}>
                <div style={{flex: 1, fontWeight: 600}}>
                    {savedSearch.isLoading
                        ? <Spinner size={SpinnerSize.small} />
                        : (savedSearch.data?.name ?? 'Saved Search')}
                </div>
                <IconButton
                    iconProps={{iconName: 'Delete'}}
                    title='Remove saved search from bundle'
                    onClick={props.onRemove}
                />
            </div>
            <DataGrid
                getItemId={(item) => item.id ?? ''}
                items={[displayItem]}
                loading={savedSearch.isLoading || results.isLoading}
                overrideColumns={columns}
                selectionMode={GridSelectionMode.none}
                useContentHeight={true}
            />
        </div>
    );
};

export default SavedSearchBundleItemRow;
